import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';

import belowIcon from '../../asset/icon/below.png';
import defaultImg from '../../asset/project/default_1.png';
import { convertWorkSizeToString, WorkSimpleWithDetails } from '../../shared/dto/EntityRepository';
import { usesWorkDuration, WorkContentMode } from '../../shared/enum/EnumRepository';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useSettingStore } from '../../shared/store/settingStore';
import { resolveUploadFileOriginUrl } from '../../shared/util/normalizeWorkData';
import { shrinkString } from '../../shared/util/shrinkString';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';

import BlockWithVisible from './BlockWithVisible';
import SimpleWorkDetailForFormationItem from './SimpleWorkDetailForFormationItem';
import { useCarouselSlotImport } from './useCarouselSlotImport';
import { useFormationSlotImport } from './useFormationSlotImport';

interface SimpleWorkForFormationItemProps {
  data: WorkSimpleWithDetails;
  marqueeHitIds?: Set<string>;
}

const SimpleWorkForFormationItem: React.FC<SimpleWorkForFormationItemProps> = ({
  data,
  marqueeHitIds,
}) => {
  const { getSimpleWorkDetailList } = useWorkDetailActions();
  const formationImport = useFormationSlotImport();
  const carouselImport = useCarouselSlotImport();
  const { importToSlot, isBulkImport, isSelected } = carouselImport.isActive
    ? carouselImport
    : formationImport;
  const setting = useSettingStore((s) => s.setting);
  const showArtist = shouldShowWorkArtist(setting);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(resolveUploadFileOriginUrl(data.uploadFile), imgContainerRef);
  const workSelection = {
    workId: data.id,
    originUrl: resolveUploadFileOriginUrl(data.uploadFile),
  };
  const isWorkSelected = isBulkImport && isSelected(workSelection);
  const isWorkLocked = isWorkSelected
    || (carouselImport.isActive && carouselImport.isAlreadyInCarousel(workSelection));

  const handleRowClick = () => {
    importToSlot(
      {
        workId: data.id,
        originUrl: resolveUploadFileOriginUrl(data.uploadFile),
        title: data.title,
        prodYear: data.prodYear,
        material: data.material,
        size: data.size,
        sizeText: data.sizeText,
        dimensionsType: data.dimensionsType,
        duration: data.duration,
        artist: data.artist,
      },
      data,
    );
  };

  const shrinkedTitle = useMemo(() => shrinkString(data.title, 24), [data.title]);
  const artistName = showArtist
    ? resolveWorkArtistName(data.artist, WorkContentMode.Orig)
    : '';
  const shrinkedArtist = useMemo(
    () => (artistName ? shrinkString(artistName, 16) : '-'),
    [artistName],
  );
  const sizeString = useMemo(() => {
    if (usesWorkDuration(data.workType)) {
      return data.duration || '-';
    }
    return convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText, 'Variable');
  }, [data.size, data.sizeText, data.dimensionsType, data.workType, data.duration]);

  const onClickDetailHandler = async (workId: string) => {
    await getSimpleWorkDetailList(workId);
  };

  return (
    <>
      <SimpleWorkForFormationItemComp
        key={data.id}
        data-import-row-id={`work:${data.id}`}
        $isSelected={isWorkLocked}
        $isMarqueeHit={!!marqueeHitIds?.has(`work:${data.id}`) && !isWorkLocked}
        aria-selected={isBulkImport || carouselImport.isActive ? isWorkLocked : undefined}
        onClick={handleRowClick}
      >
        <ImageBlock>
          <ImgWrapper ref={imgContainerRef}>
            <WorkImage src={imageUrl === '' ? defaultImg : imageUrl} alt={data.title} />
          </ImgWrapper>
        </ImageBlock>
        {showArtist && (
          <ArtistBlock>
            <span>{shrinkedArtist}</span>
          </ArtistBlock>
        )}
        <TitleBlock>
          <span>{shrinkedTitle}</span>
        </TitleBlock>
        <BlockWithVisible width={'0.6'} value={data.prodYear || '-'} />
        <BlockWithVisible width={'1'} value={sizeString} />
        <BtnWrapper
          data-suppress-marquee
          $isEnabled={data.hasDetail && data.detailList.length === 0}
          onClick={(e) => {
            e.stopPropagation();
            data.hasDetail && onClickDetailHandler(data.id);
          }}
        >
          {data.hasDetail && data.detailList.length === 0 ? (
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent={true}
              backgroundImg={belowIcon}
              onClick={(e) => {
                e.stopPropagation();
                onClickDetailHandler(data.id);
              }}
              aria-label="Show work details"
            />
          ) : (
            '-'
          )}
        </BtnWrapper>
      </SimpleWorkForFormationItemComp>
      <SimpleWorkForFormationDetailContainer $detailCount={data.detailList.length}>
        {data.detailList.map((detail) => (
          <SimpleWorkDetailForFormationItem
            key={detail.id}
            parentWork={data}
            data={detail}
            showArtist={showArtist}
            artistName={shrinkedArtist}
            isMarqueeHit={!!marqueeHitIds?.has(`detail:${detail.id}`)}
          />
        ))}
      </SimpleWorkForFormationDetailContainer>
    </>
  );
};

const SimpleWorkForFormationItemComp = styled.div<{ $isSelected: boolean; $isMarqueeHit?: boolean }>`
  width: 100%;
  height: fit-content;
  min-height: 50px;
  max-height: 70px;
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s ease-in-out;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  box-shadow: ${({ theme, $isMarqueeHit }) => (
    $isMarqueeHit ? `inset 0 0 0 1px ${theme.colors.color_Gray_05}` : 'none'
  )};
  opacity: ${({ $isSelected }) => ($isSelected ? 0.5 : 1)};
  cursor: ${({ $isSelected }) => ($isSelected ? 'not-allowed' : 'pointer')};
`;

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 60%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ArtistBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 4px;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleBlock = styled.div`
  flex: 2;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const BtnWrapper = styled.div<{ $isEnabled?: boolean }>`
  flex: 0.2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isEnabled }) => ($isEnabled ? 'pointer' : 'default')};
  ${({ theme }) => theme.typography.Body_04};
`;

const SimpleWorkForFormationDetailContainer = styled.div<{ $detailCount: number }>`
  width: 100%;
  height: ${({ $detailCount }) => $detailCount * 50}px;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: height 0.8s ease-in-out;
`;

export default SimpleWorkForFormationItem;
